import { FiInfo } from "solid-icons/fi";
import { type Component, Match, Show, Switch } from "solid-js";
import { useI18n } from "@/api/i18n";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useConfidenceScoreExplanation } from "../api";
import type { ConfidenceScoreBreakdown } from "../types";
import { MatchingBadge } from "./MatchingBadge";

type ConfidenceScoreBadgeProps = {
  score: number;
  breakdown?: ConfidenceScoreBreakdown;
};

const ConfidenceScoreExplanation: Component<{
  breakdown: ConfidenceScoreBreakdown;
}> = (props) => {
  const { t } = useI18n();
  const explanationQuery = useConfidenceScoreExplanation(() => props.breakdown);

  const loadExplanation = () => {
    if (
      explanationQuery.data !== undefined ||
      explanationQuery.fetchStatus === "fetching"
    ) {
      return;
    }

    void explanationQuery.refetch();
  };

  return (
    <Tooltip openDelay={150}>
      <TooltipTrigger
        type="button"
        aria-label={t(
          "company.search.freelancers.result.score_explanation.label",
        )}
        class="inline-flex h-5 w-5 shrink-0 cursor-help items-center justify-center rounded-full text-gray-500 transition-[color,background-color,box-shadow,scale] hover:bg-gray-100 hover:text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.97]"
        onMouseEnter={loadExplanation}
        onFocus={loadExplanation}
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <FiInfo class="h-4 w-4" aria-hidden="true" />
      </TooltipTrigger>
      <TooltipContent class="max-w-sm p-3 text-sm leading-relaxed">
        <Switch>
          <Match when={explanationQuery.isFetching}>
            <div class="flex items-center gap-2" aria-live="polite">
              <span
                data-testid="confidence-score-loading"
                class="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-primary/30 border-t-primary motion-reduce:animate-none"
              />
              <span>
                {t(
                  "company.search.freelancers.result.score_explanation.loading",
                )}
              </span>
            </div>
          </Match>
          <Match when={explanationQuery.isSuccess}>
            <p aria-live="polite">{explanationQuery.data}</p>
          </Match>
          <Match when={explanationQuery.isError}>
            <p class="text-destructive" role="alert">
              {t("company.search.freelancers.result.score_explanation.error")}
            </p>
          </Match>
        </Switch>
      </TooltipContent>
    </Tooltip>
  );
};

export const ConfidenceScoreBadge: Component<ConfidenceScoreBadgeProps> = (
  props,
) => (
  <span class="inline-flex items-center gap-1">
    <MatchingBadge score={props.score} />
    <Show when={props.breakdown} keyed>
      {(breakdown) => <ConfidenceScoreExplanation breakdown={breakdown} />}
    </Show>
  </span>
);
